import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import type { Asset, Message, Project, ReferenceItem, Session, TerminalRunHydration } from "../../api/types.js";
import { DEFAULT_SESSION_TITLE, projectDisplayName } from "../shared/constants.js";
import type { ThreadScrollController } from "../chat/useThreadScroll.js";
import type { UseSourceDrawerState } from "../sources/useSourceDrawer.js";
import { createProject, deleteProject as deleteProjectRequest, renameProject as renameProjectRequest } from "../projects/projectApi.js";
import {
  createSession as createSessionRequest,
  deleteSession as deleteSessionRequest,
  getSessionDetail,
  getSessionReferencePage,
  getSessionReferenceSummary,
  listSessions as listSessionsRequest,
  updateSession
} from "./sessionApi.js";
import { mergeReferenceEntities } from "../sources/citationSources.js";
import { subscribeWorkFavorites } from "../works/workApi.js";

const MAX_VISIBLE_SESSION_ASSETS = 50;
const MAX_VISIBLE_SESSION_MESSAGES = 200;

export type SessionScrollMode = "bottom" | "preserve" | "none";

export type WorkspacePanelActions = {
  closeFloatingPanels: () => void;
  closeAllPanels: () => void;
  clearPrompt: () => void;
};

export type UseSessionsState = {
  projects: Project[];
  setProjects: Dispatch<SetStateAction<Project[]>>;
  sessions: Session[];
  setSessions: Dispatch<SetStateAction<Session[]>>;
  currentProjectId: string;
  setCurrentProjectId: Dispatch<SetStateAction<string>>;
  currentSession: Session | null;
  setCurrentSession: Dispatch<SetStateAction<Session | null>>;
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  citationSources: ReferenceItem[];
  setCitationSources: Dispatch<SetStateAction<ReferenceItem[]>>;
  referencePool: ReferenceItem[];
  setReferencePool: Dispatch<SetStateAction<ReferenceItem[]>>;
  referencePoolTotalCount: number;
  referencePoolNextCursor: string | null;
  referencePoolLoading: boolean;
  referencePoolLoaded: boolean;
  referencePoolError: string;
  assets: Asset[];
  setAssets: Dispatch<SetStateAction<Asset[]>>;
  events: any[];
  setEvents: Dispatch<SetStateAction<any[]>>;
  expandedActivityMessageId: string | null;
  setExpandedActivityMessageId: Dispatch<SetStateAction<string | null>>;
  loadingSessionId: string | null;
  isSessionLoading: boolean;
  scrollToBottomSignal: number;
  showNewChat: (projectId?: string) => void;
  openSession: (session: Session, options?: { immediate?: boolean; scroll?: SessionScrollMode }) => Promise<Session | null>;
  openSessionById: (sessionId: string, options?: { immediate?: boolean; scroll?: SessionScrollMode }) => Promise<Session | null>;
  loadReferencePoolPage: (reset?: boolean) => Promise<void>;
  refreshReferencePoolSummary: (sessionId?: string) => Promise<void>;
  invalidateReferencePool: (sessionId?: string) => void;
  restoreRunMessages: (sessionId: string, assistantMessageId: string, hydration: TerminalRunHydration) => boolean;
  loadSessions: () => Promise<Session[]>;
  loadProject: (projectId: string) => Promise<void>;
  createPersistedSession: () => Promise<Session>;
  addProject: () => Promise<void>;
  renameProject: (project: Project) => Promise<void>;
  deleteProject: (project: Project) => Promise<void>;
  renameSession: (session: Session) => Promise<void>;
  moveSession: (session: Session, projectId: string | null) => Promise<void>;
  togglePinSession: (session: Session) => Promise<void>;
  deleteSession: (session: Session) => Promise<void>;
};

export function useSessions({
  scrollRef,
  sourceDrawerRef,
  panelActionsRef
}: {
  scrollRef: RefObject<ThreadScrollController | null>;
  sourceDrawerRef: RefObject<UseSourceDrawerState | null>;
  panelActionsRef: RefObject<WorkspacePanelActions | null>;
}): UseSessionsState {
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState("");
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [citationSources, setCitationSources] = useState<ReferenceItem[]>([]);
  const [referencePool, setReferencePool] = useState<ReferenceItem[]>([]);
  const [referencePoolTotalCount, setReferencePoolTotalCount] = useState(0);
  const [referencePoolNextCursor, setReferencePoolNextCursor] = useState<string | null>(null);
  const [referencePoolLoading, setReferencePoolLoading] = useState(false);
  const [referencePoolLoaded, setReferencePoolLoaded] = useState(false);
  const [referencePoolError, setReferencePoolError] = useState("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [expandedActivityMessageId, setExpandedActivityMessageId] = useState<string | null>(null);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const [scrollToBottomSignal, setScrollToBottomSignal] = useState(0);
  const sessionLoadRequest = useRef(0);
  const referencePoolLoadRequest = useRef(0);
  const referencePoolSummaryRequest = useRef(0);
  const referencePoolLoadingRef = useRef(false);
  const currentSessionIdRef = useRef<string | null>(null);
  const knownSourcesRef = useRef<ReferenceItem[]>([]);
  currentSessionIdRef.current = currentSession?.id || null;
  knownSourcesRef.current = [...citationSources, ...referencePool];
  const isSessionLoading = Boolean(loadingSessionId && currentSession?.id === loadingSessionId);

  useEffect(() => subscribeWorkFavorites(({ workId, isSaved, sessionId }) => {
    const updateSource = (source: ReferenceItem) => source.workId === workId ? { ...source, isSaved } : source;
    setCitationSources((old) => old.map(updateSource));
    setReferencePool((old) => old.map(updateSource));
    setAssets((old) => {
      const favoriteId = `favorite:${workId}`;
      if (!isSaved) return old.filter((asset) => asset.id !== favoriteId);
      if (!sessionId || sessionId !== currentSessionIdRef.current) return old;
      if (old.some((asset) => asset.id === favoriteId)) return old;
      const source = knownSourcesRef.current.find((item) => item.workId === workId);
      if (!source || !currentSessionIdRef.current) return old;
      return [{
        id: favoriteId,
        workId,
        title: source.title,
        kind: "paper",
        url: source.url,
        meta: { authors: source.authors, year: source.year, venue: source.venue, isSaved: true },
        createdAt: new Date().toISOString()
      }, ...old];
    });
  }), []);

  function resetConversationState() {
    referencePoolLoadRequest.current += 1;
    referencePoolSummaryRequest.current += 1;
    setMessages([]);
    setCitationSources([]);
    setReferencePool([]);
    setReferencePoolTotalCount(0);
    setReferencePoolNextCursor(null);
    setReferencePoolLoading(false);
    referencePoolLoadingRef.current = false;
    setReferencePoolLoaded(false);
    setReferencePoolError("");
    setAssets([]);
    setEvents([]);
    setExpandedActivityMessageId(null);
    sourceDrawerRef.current?.resetSourceSelection();
  }

  function closeAllPanels() {
    panelActionsRef.current?.closeAllPanels();
  }

  function showNewChat(projectId = "") {
    sessionLoadRequest.current += 1;
    scrollRef.current?.resumeThreadFollow();
    setCurrentProjectId(projectId);
    setCurrentSession(null);
    resetConversationState();
    setLoadingSessionId(null);
    panelActionsRef.current?.clearPrompt();
    closeAllPanels();
  }

  function applySessionDetail(data: Awaited<ReturnType<typeof getSessionDetail>>, scrollMode: SessionScrollMode, preservedScrollTop: number) {
    setCurrentProjectId(data.session.projectId || "");
    setCurrentSession(data.session);
    setMessages(data.messages);
    setCitationSources(data.citationSources || []);
    if (sourceDrawerRef.current?.sourceMessageId && !data.messages.some((message) => message.id === sourceDrawerRef.current?.sourceMessageId)) {
      sourceDrawerRef.current.resetSourceSelection();
    }
    referencePoolLoadRequest.current += 1;
    referencePoolSummaryRequest.current += 1;
    referencePoolLoadingRef.current = false;
    setReferencePool([]);
    setReferencePoolTotalCount(Number(data.referencePoolSummary?.totalCount || 0));
    setReferencePoolNextCursor(null);
    setReferencePoolLoading(false);
    setReferencePoolLoaded(false);
    setReferencePoolError("");
    setAssets(data.assets || []);
    setLoadingSessionId(null);
    panelActionsRef.current?.closeFloatingPanels();
    if (scrollMode === "bottom") {
      setScrollToBottomSignal((value) => value + 1);
    } else if (scrollMode === "preserve") {
      setTimeout(() => {
        const scroll = scrollRef.current;
        const container = scroll?.threadScrollContainer();
        if (!scroll || !container) return;
        if (Math.abs(container.scrollTop - preservedScrollTop) > 8) return;
        const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
        container.scrollTo({ top: Math.min(preservedScrollTop, maxTop), behavior: "auto" });
        scroll.lastThreadScrollTopRef.current = container.scrollTop;
      }, 30);
    }
  }

  async function openSession(session: Session, options: { immediate?: boolean; scroll?: SessionScrollMode } = {}) {
    const immediate = options.immediate ?? true;
    const scrollMode = options.scroll || "bottom";
    const preservedScrollTop = scrollMode === "preserve" ? scrollRef.current?.threadScrollContainer()?.scrollTop ?? 0 : 0;
    const requestId = ++sessionLoadRequest.current;
    if (immediate) {
      scrollRef.current?.resumeThreadFollow();
      setCurrentProjectId(session.projectId || "");
      setCurrentSession(session);
      resetConversationState();
      setLoadingSessionId(session.id);
      closeAllPanels();
      setTimeout(() => scrollRef.current?.threadScrollContainer()?.scrollTo({ top: 0 }), 0);
    }
    try {
      const data = await getSessionDetail(session.id);
      if (sessionLoadRequest.current !== requestId) return null;
      applySessionDetail(data, scrollMode, preservedScrollTop);
      return data.session;
    } catch (error) {
      if (sessionLoadRequest.current === requestId) setLoadingSessionId(null);
      throw error;
    }
  }

  async function openSessionById(sessionId: string, options: { immediate?: boolean; scroll?: SessionScrollMode } = {}) {
    if (!sessionId) return null;
    // The active session can be missing from the filtered sidebar list, so it
    // is loaded directly by ID without a synthetic Session or URL change.
    if (currentSessionIdRef.current === sessionId && !options.immediate) {
      return currentSession?.id === sessionId ? currentSession : null;
    }
    const scrollMode = options.scroll || "bottom";
    const preservedScrollTop = scrollMode === "preserve" ? scrollRef.current?.threadScrollContainer()?.scrollTop ?? 0 : 0;
    const requestId = ++sessionLoadRequest.current;
    scrollRef.current?.resumeThreadFollow();
    setLoadingSessionId(sessionId);
    try {
      const data = await getSessionDetail(sessionId);
      if (sessionLoadRequest.current !== requestId) return null;
      applySessionDetail(data, scrollMode, preservedScrollTop);
      return data.session;
    } catch (error) {
      if (sessionLoadRequest.current === requestId) setLoadingSessionId(null);
      throw error;
    }
  }

  async function loadSessions() {
    const loadedSessions = await listSessionsRequest();
    setSessions(loadedSessions);
    return loadedSessions;
  }

  async function loadReferencePoolPage(reset = false) {
    const sessionId = currentSessionIdRef.current;
    if (!sessionId || referencePoolLoadingRef.current) return;
    if (!reset && referencePoolLoaded && !referencePoolNextCursor) return;
    const requestId = ++referencePoolLoadRequest.current;
    const cursor = reset ? null : referencePoolNextCursor;
    referencePoolLoadingRef.current = true;
    setReferencePoolLoading(true);
    setReferencePoolError("");
    try {
      const data = await getSessionReferencePage(sessionId, cursor);
      if (referencePoolLoadRequest.current !== requestId || currentSessionIdRef.current !== sessionId) return;
      setReferencePool((old) => mergeReferenceEntities(reset ? [] : old, data.items || []));
      referencePoolSummaryRequest.current += 1;
      setReferencePoolTotalCount(Number(data.totalCount || 0));
      setReferencePoolNextCursor(data.nextCursor || null);
      setReferencePoolLoaded(true);
    } catch (error) {
      if (referencePoolLoadRequest.current !== requestId || currentSessionIdRef.current !== sessionId) return;
      setReferencePoolError(error instanceof Error ? error.message : "文献池加载失败");
    } finally {
      if (referencePoolLoadRequest.current === requestId) {
        referencePoolLoadingRef.current = false;
        setReferencePoolLoading(false);
      }
    }
  }

  async function refreshReferencePoolSummary(sessionId = currentSessionIdRef.current || "") {
    if (!sessionId || currentSessionIdRef.current !== sessionId) return;
    const requestId = ++referencePoolSummaryRequest.current;
    const data = await getSessionReferenceSummary(sessionId);
    if (referencePoolSummaryRequest.current !== requestId || currentSessionIdRef.current !== sessionId) return;
    setReferencePoolTotalCount(Number(data.totalCount || 0));
  }

  function invalidateReferencePool(sessionId = currentSessionIdRef.current || "") {
    if (!sessionId || currentSessionIdRef.current !== sessionId) return;
    referencePoolLoadRequest.current += 1;
    referencePoolLoadingRef.current = false;
    setReferencePool([]);
    setReferencePoolNextCursor(null);
    setReferencePoolLoading(false);
    setReferencePoolLoaded(false);
    setReferencePoolError("");
  }

  function restoreRunMessages(sessionId: string, assistantMessageId: string, hydration: TerminalRunHydration) {
    if (currentSessionIdRef.current !== sessionId) return false;
    if (!hydration.messages.some((message) => message.id === assistantMessageId && message.role === "assistant")) return false;
    const hydratedIds = new Set(hydration.messages.map((message) => message.id));
    const hydratedRunRoles = new Set(hydration.messages
      .filter((message) => message.runId)
      .map((message) => `${message.runId}:${message.role}`));
    setMessages((old) => [
      ...old.filter((message) => !hydratedIds.has(message.id) && !hydratedRunRoles.has(`${message.runId}:${message.role}`)),
      ...hydration.messages
    ].slice(-MAX_VISIBLE_SESSION_MESSAGES));
    setCitationSources((old) => mergeReferenceEntities(old, hydration.citationSources || []));
    setAssets((old) => {
      const hydratedIds = new Set((hydration.assets || []).map((asset) => asset.id));
      return [...(hydration.assets || []), ...old.filter((asset) => !hydratedIds.has(asset.id))]
        .slice(0, MAX_VISIBLE_SESSION_ASSETS);
    });
    return true;
  }

  async function loadProject(projectId: string) {
    showNewChat(projectId);
    await loadSessions();
  }

  async function createPersistedSession() {
    const session = await createSessionRequest({ title: DEFAULT_SESSION_TITLE });
    setCurrentProjectId(session.projectId || "");
    setCurrentSession(session);
    resetConversationState();
    setSessions((old) => old.some((item) => item.id === session.id) ? old : [session, ...old]);
    panelActionsRef.current?.closeFloatingPanels();
    return session;
  }

  async function addProject() {
    const name = window.prompt("项目名称");
    if (!name) return;
    const project = await createProject(name);
    setProjects((old) => [project, ...old]);
    setCurrentProjectId(project.id);
  }

  async function renameProject(project: Project) {
    const nextName = window.prompt("重命名项目", projectDisplayName(project));
    if (nextName === null) return;
    const name = nextName.trim();
    if (!name) return;
    const renamedProject = await renameProjectRequest(project.id, name);
    setProjects((old) => old.map((item) => item.id === renamedProject.id ? renamedProject : item));
  }

  async function deleteProject(project: Project) {
    if (!window.confirm("删除项目？项目内对话会回到最近列表，不会被删除。")) return;
    await deleteProjectRequest(project.id);
    setProjects((old) => old.filter((item) => item.id !== project.id));
    if (currentProjectId === project.id) setCurrentProjectId("");
    if (currentSession?.projectId === project.id) setCurrentSession({ ...currentSession, projectId: null });
    await loadSessions();
  }

  async function renameSession(session: Session) {
    const nextTitle = window.prompt("重命名这个聊天", session.title || "");
    if (nextTitle === null) return;
    const title = nextTitle.trim();
    if (!title) return;
    const renamedSession = await updateSession(session.id, { title });
    if (currentSession?.id === renamedSession.id) setCurrentSession(renamedSession);
    await loadSessions();
  }

  async function moveSession(session: Session, projectId: string | null) {
    const movedSession = await updateSession(session.id, { projectId });
    if (currentSession?.id === movedSession.id) {
      setCurrentProjectId(movedSession.projectId || "");
      setCurrentSession(movedSession);
    }
    await loadSessions();
  }

  async function togglePinSession(session: Session) {
    const pinnedSession = await updateSession(session.id, { pinned: !session.pinnedAt });
    if (currentSession?.id === pinnedSession.id) setCurrentSession(pinnedSession);
    await loadSessions();
  }

  async function deleteSession(session: Session) {
    if (!window.confirm("删除这个聊天？")) return;
    await deleteSessionRequest(session.id);
    setSessions((old) => old.filter((item) => item.id !== session.id));
    if (currentSession?.id === session.id) showNewChat("");
    await loadSessions();
  }

  return {
    projects,
    setProjects,
    sessions,
    setSessions,
    currentProjectId,
    setCurrentProjectId,
    currentSession,
    setCurrentSession,
    messages,
    setMessages,
    citationSources,
    setCitationSources,
    referencePool,
    setReferencePool,
    referencePoolTotalCount,
    referencePoolNextCursor,
    referencePoolLoading,
    referencePoolLoaded,
    referencePoolError,
    assets,
    setAssets,
    events,
    setEvents,
    expandedActivityMessageId,
    setExpandedActivityMessageId,
    loadingSessionId,
    isSessionLoading,
    scrollToBottomSignal,
    showNewChat,
    openSession,
    openSessionById,
    loadReferencePoolPage,
    refreshReferencePoolSummary,
    invalidateReferencePool,
    restoreRunMessages,
    loadSessions,
    loadProject,
    createPersistedSession,
    addProject,
    renameProject,
    deleteProject,
    renameSession,
    moveSession,
    togglePinSession,
    deleteSession
  };
}
